import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { getRequest } from '../../../call-center/services/restService';
import postLeadService from '../../Services/postLeadService';
import putLeadService from '../../Services/putLeadService';

import { AdPartner } from '../../Components/AdPartner';
import StepProgressBar from '../../Components/StepProgressBar';
import SureHits from '../../Components/SureHits';
import { Button } from '../../Components/ui';

import isSet from '../../Helpers/isSet';
import { addJornayaScript, createJornayaInput, JORNAYA_INPUT_ID } from '../../Helpers/jornaya';

import { isFilledWith, isObjectValid } from '../../Helpers/validation';

import { useFlag } from '@Helpers/featureFlags';
import content from '../../../../../public/js/my-quote-content.json';
import ThankYou from '../../Components/ThankYou';
import gmtPhoneAndEmail from '../../Helpers/gtmLastStep';
import { QUOTE_TYPES } from '../../Helpers/surehits';
import getParameterizableQuoteFieldService from '../../Services/getParameterizableQuoteField';
import CallToActionFooter from './Components/CallToActionFooter';
import DisclosureText from './Components/DisclosureText';
import FormInputs from './Components/FormInputs';
import Recaptcha from './Components/Recaptcha';

const submitLeadStepIndex = 2;

const getStartingStep = (paramStep, numberOfSteps) => {
    let newStep = parseInt(paramStep || 1);
    if (isNaN(newStep) || newStep < 1 || newStep > numberOfSteps) {
        newStep = 1;
    }
    return newStep;
};

const MyQuote = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const myQuoteRef = useRef(null);

    const [waitForData, setWaitForData] = useState(sessionStorage.getItem('formData') ? true : false);
    const [formData, setFormData] = useState(location.state || {});
    const [step, setStep] = useState(getStartingStep(searchParams.get('step'), content.length));
    const [showError, setShowError] = useState(false);
    const [showSureHits, setShowSureHits] = useState(false);
    const [recaptchaSolved, setRecaptchaSolved] = useState(false);
    const [leadSource, setLeadSource] = useState('');
    const [formLocked, setFormLocked] = useState(false);
    const [pageAds, setPageAds] = useState([]);
    const [parameterizableQuoteFields, setParameterizableQuoteFields] = useState([]);
    const stepFromURL = parseInt(searchParams.get('step')) || 1;
    const isLastStep = step === content.length;
    const quoteType = QUOTE_TYPES.COMMERCIAL;
    let insurance_types_param = [];

    // recaptcha
    const recaptchaSiteKey = global.recaptchaSiteKey;
    const showRecaptcha = step === 2 && recaptchaSiteKey;
    const isCi4948FlagEnabled = useFlag('Crm.UpdatePlacementTCPADisclosureOnSite.CI-4948');

    useEffect(() => {
        const passedFormData = JSON.parse(sessionStorage.getItem('formData') || '{}');
        let uuid = Date.now();

        if (formData.uuid) {
            uuid = formData.uuid;
        }

        if (isObjectValid(passedFormData) && isFilledWith(passedFormData, 'zipcode', 'insurance_types')) {
            // This is the data passed from the index page,
            // in this case we want to start the user on step 2
            setFormData({ ...passedFormData, uuid });
            setTimeout(() => navigate(null, { replace: true, state: passedFormData }), 1);
            setTimeout(() => navigate('?step=2', { replace: false, state: passedFormData }), 1);
            insurance_types_param = passedFormData.insurance_types;
        } else {
            if (Object.keys(formData).length === 0) {
                //remove the start_page cookie if user is not coming from another page.
                document.cookie = 'start_page=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                if (window.page_id) {
                    var expiryDate = new Date();
                    expiryDate.setTime(expiryDate.getTime() + 45 * 60 * 1000); // 45 minutes from now
                    document.cookie = `start_page=${window.page_id}; expires=` + expiryDate.toUTCString() + '; path=/';
                }
                setTimeout(() => navigate('?step=1', { replace: true, state: formData }), 1);
            }

            insurance_types_param = formData.insurance_types;
            setFormData({ ...formData, uuid });
        }

        const fetchParameterizableQuoteFields = async () => {
            const fields = await getParameterizableQuoteFieldService(insurance_types_param);
            setParameterizableQuoteFields(fields);
        };

        fetchParameterizableQuoteFields();
        setWaitForData(false);
        sessionStorage.removeItem('formData');
    }, []);

    useEffect(() => {
        const formCompleted = JSON.parse(sessionStorage.getItem('formCompleted') || '{}');
        if (isLastStep && formData.uuid !== undefined) {
            sessionStorage.setItem('formCompleted', JSON.stringify({ ...formCompleted, [formData.uuid]: true }));
        } else if (formData.uuid !== undefined && formCompleted[formData.uuid]) {
            setFormLocked(true);
            const gotoURL = formData.quoteOrigin || '/';
            window.location.href = gotoURL;
        }
    }, [isLastStep]);

    useEffect(() => {
        // Update step state based on query params
        let newStep = parseInt(searchParams.get('step') || 1);

        // Clamp the step so it stays within the bounds of step 1 to maxSteps(content.length)
        if (isNaN(newStep) || newStep < 1 || newStep > content.length) {
            newStep = 1;
            navigate(`?step=1`, { replace: true });
        }

        const myQuoteElement = document.getElementById('my-quote');
        if (myQuoteElement) {
            const stepClasses = Array.from(myQuoteElement.classList).filter(className => className.startsWith('step'));
            stepClasses.forEach(className => myQuoteElement.classList.remove(className));
            myQuoteElement.classList.add(`step${newStep}`);
        }
        setStep(newStep);
    }, [stepFromURL]);

    useEffect(() => {
        // When the step changes, focus on the heading section
        myQuoteRef.current.focus();
        if (isLastStep) {
            setShowSureHits(true);
            gmtPhoneAndEmail(formData.contact_phone, formData.email);
        } else {
            setShowSureHits(false);
        }
    }, [step]);

    useEffect(() => {
        // jornaya compliance script
        addJornayaScript();

        /* this element gets populated with the leadid token value, which we send to the create lead API */
        createJornayaInput('lead-form'); // Moved input out of react to address CI-3186

        setTimeout(() => {
            const leadIdToken = document.getElementById(JORNAYA_INPUT_ID)?.value;
            setFormData(prevData => ({
                ...prevData,
                jornaya_id: leadIdToken,
            }));
        }, 1000);
    }, []);

    useEffect(() => {
        if (formData.id && !location.state?.id) {
            // if formdata changes and we have an id only in state, update location state
            setTimeout(() => navigate(null, { replace: true, state: { ...formData } }), 1);
        }
    }, [formData]);

    /*
    This fetches data from an API endpoint based on a given formData id, then sets
    the retrieved data in the state variable setPageAds.
  */
    useEffect(() => {
        const fetchData = async () => {
            if (formData.id) {
                getRequest(
                    `/api/pageAds/${window?.Laravel?.page?.id}/lead/${formData.id}`,
                    response => {
                        setPageAds(response);
                    },
                    error => {
                        console.error('Error fetching data:', error);
                    }
                );
            }
        };

        fetchData();
    }, [formData.id]);

    const handleStepChange = async type => {
        setShowError(false);
        if (type === 'next') {
            const parametric_fields = await getParameterizableQuoteFieldService(formData.insurance_types);
            setParameterizableQuoteFields(parametric_fields);

            const stepFields = (content[step - 1] || []).inputs.map(input => input.name);
            const requiredFields =
                (content[step - 1].inputs &&
                    content[step - 1].inputs
                        .filter(input => {
                            if (input.type === 'parameterizable') {
                                const parametricField = parametric_fields.find(item => item.name === input.name);
                                if (parametricField) {
                                    return parametricField.render && parametricField.required;
                                }
                            }

                            return input.required;
                        })
                        .map(input => input.name)) ||
                [];

            const missingFields = requiredFields.filter(field => {
                const fieldValue = formData[field];
                return !isSet(fieldValue);
            });
            // create a list of fields that are invalid in the current step
            const stepInvalidFields = stepFields.filter(field => {
                return (formData?.invalidFields || []).includes(field);
            });

            if (missingFields.length > 0 || stepInvalidFields.length > 0) {
                setTimeout(() => {
                    setShowError(true);
                }, 100);
                return;
            }
        }

        let nextStep = type === 'next' ? step + 1 : step - 1;
        let submissionResponse = null;
        if (type === 'next') {
            submissionResponse = await handleLeadSubmission(nextStep);
        }

        if (submissionResponse && submissionResponse.redirectTo) {
            window.location.href = submissionResponse.redirectTo;
        } else {
            setStep(nextStep);
            navigate(`?step=${nextStep}`, { replace: false, state: formData });
        }
    };

    const handleLeadSubmission = async nextStep => {
        // If we have a lead ID we want to update the lead data on step change
        // If we don't have a lead ID we want to create a new lead when we reach the lead capture step
        const { id: leadId } = formData;
        const goingToLastStep = nextStep === content.length;
        const sanitizeRequestData = formData => {
            if (formData.insurance_types) {
                let requestData = {
                    ...formData,
                    insurance_types: [...new Set(formData.insurance_types.map(type => type.id))],
                };

                if (requestData.insured_by_progressive) {
                    requestData.insured_by_progressive = requestData.insured_by_progressive === 'Yes';
                }

                return requestData;
            }
            return formData;
        };
        const updateLeadService = formData => {
            if (leadId) {
                return putLeadService(leadId, formData, goingToLastStep);
            } else {
                // not the lead capture step and no lead id, no operation
                return Promise.resolve('no-operation');
            }
        };

        const service = nextStep === submitLeadStepIndex + 1 && !leadId ? postLeadService : updateLeadService;
        try {
            if (!formData?.origin_page_id) {
                // If we don't have an origin page id, we want to set it to the current page id
                formData.origin_page_id = window?.page_id;
            }
            const response = await service(sanitizeRequestData(formData));
            if (response === 'no-operation') {
                return Promise.resolve();
            }

            if (!response.ok) {
                console.error({ message: 'Failed to create lead' }); // Will display a message in a follow up story.
                return Promise.resolve();
            }

            const contentType = response.headers.get('content-type');
            let data;
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                data = JSON.parse(await response.text());
            }

            if (data.hasOwnProperty('lead')) {
                setFormData(prevData => ({ ...prevData, id: data.lead.id, sicCode: data.lead.sic_code }));
            } else if (data?.id) {
                setFormData(prevData => ({ ...prevData, id: data.id }));
            }

            if (data.hasOwnProperty('metadata')) {
                let text = data.metadata.confirmation_text;
                // If we fail to parse the JSON, we'll just display the text as is in the heading
                // added for holiday text.
                try {
                    text = JSON.parse(text);
                } catch (e) {
                    if (typeof text === 'string') {
                        text = [text];
                    } else {
                        text = [];
                    }
                }
                const [heading, subtext1, subtext2, callToAction = {}] = text;
                const thankYouContent = { heading, subtext1, subtext2, callToAction };
                const tcpaDisclosure = data.metadata?.tcpa_disclosure || '';
                setFormData(prevData => ({ ...prevData, thankYouContent, tcpaDisclosure }));
            }
            setLeadSource(data?.lead?.lead_source);

            if (nextStep === 3 && data.hasOwnProperty('action')) {
                if (data.action.type === 'redirect') {
                    return Promise.resolve({ redirectTo: data.action.url });
                }
            }
        } catch (error) {
            console.error(error);
            return Promise.resolve();
        }
    };

    const handleInputChange = ({ type, name, element, isValid = true }) => {
        setFormData(prevData => {
            let thisInvalidList = prevData.invalidFields || [];
            if (!isValid) {
                if (!thisInvalidList.includes(name)) {
                    thisInvalidList.push(name);
                }
            } else {
                thisInvalidList = thisInvalidList.filter(invalid => invalid !== name);
            }
            const thisData = { ...prevData, [name]: element.value, invalidFields: thisInvalidList };
            // Saves the state incase user navigates away and
            // comes back via the browser back button, url, etc.
            setTimeout(() => navigate(null, { replace: true, state: thisData }), 1);
            return thisData;
        });
    };

    const handleCaptchaChange = token => {
        if (!token) {
            return;
        }
        setRecaptchaSolved(true);
    };

    const handleSubmit = e => {
        e.preventDefault();
        handleStepChange('next');
    };

    const {
        heading = '',
        subtext1 = '',
        subtext2 = '',
        callToAction = {},
        inputs: inputFields = [],
        disclosureLabel = '',
        disclosureText = '',
    } = content[step - 1];
    const isStep2 = step === 2;
    const isStep3 = step === 3;

    const tcpaDisclosure = formData.tcpaDisclosure || '';
    return (
        <div
            style={{
                opacity: formLocked ? 0 : 1,
                pointerEvents: formLocked ? 'none' : 'auto',
            }}
            aria-hidden={formLocked}
        >
            <StepProgressBar step={step} stepCount={content.length} />
            <form id="lead-form" onSubmit={handleSubmit}>
                <div data-testid="heading-section" className="heading-section" tabIndex={-1} ref={myQuoteRef}>
                    {isLastStep && (
                        <ThankYou
                            content={formData.thankYouContent}
                            fallBack={{ heading, subtext1, subtext2, callToAction }}
                        />
                    )}
                    {!isLastStep && (
                        <div className="heading-section__text">
                            {heading && <h1>{heading}</h1>}
                            {subtext1 && <p>{subtext1}</p>}
                            {subtext2 && <p>{subtext2}</p>}
                        </div>
                    )}
                </div>
                <FormInputs
                    show={!waitForData}
                    formData={formData}
                    inputFields={inputFields}
                    showError={showError}
                    handleInputChange={handleInputChange}
                    parameterizableQuoteFields={parameterizableQuoteFields}
                />
                <Recaptcha show={showRecaptcha} callBack={handleCaptchaChange} />
                <div className="disclosure-container" style={{ display: isLastStep ? 'none' : 'inherit' }}>
                    {isStep2 && !isCi4948FlagEnabled && disclosureLabel && (
                        <p className="disclosure-label">{disclosureLabel}</p>
                    )}
                    {isStep3 && !isCi4948FlagEnabled && disclosureLabel && tcpaDisclosure && (
                        <p className="disclosure-label">{disclosureLabel}</p>
                    )}

                    {isStep2 && isCi4948FlagEnabled && <DisclosureText />}
                    {isStep3 && isCi4948FlagEnabled && tcpaDisclosure && (
                        <DisclosureText disclosureText={tcpaDisclosure} submitButtonText="Final Step" />
                    )}
                    {!isLastStep && (
                        <div className="button-container" data-testid="input-section">
                            <Button type="submit" disabled={showRecaptcha && !recaptchaSolved}>
                                {step !== content.length - 1 ? 'Next Step' : 'Final Step'}
                            </Button>
                            <Button
                                type="button"
                                onClick={e => {
                                    e.preventDefault();
                                    handleStepChange('previous');
                                }}
                                btn-type="secondary"
                                disabled={step === 1}
                                className={step === 1 ? 'hide-on-mobile' : ''}
                            >
                                Previous
                            </Button>
                        </div>
                    )}

                    {isStep2 && !isCi4948FlagEnabled && <DisclosureText />}
                    {isStep3 && !isCi4948FlagEnabled && tcpaDisclosure && (
                        <DisclosureText disclosureText={tcpaDisclosure} submitButtonText="Final Step" />
                    )}
                </div>
            </form>
            <div className="ad-container">
                {!isLastStep && <CallToActionFooter />}
                {isLastStep && (
                    <>
                        <p className="disclosure-label">Important Advertising Disclosure Below</p>
                        <div className="paid-partners">
                            {pageAds.map((ad, index) => (
                                <AdPartner key={ad.id} adIndex={index} partnerData={ad} />
                            ))}
                        </div>
                    </>
                )}
                {showSureHits && (
                    <SureHits type={quoteType} leadSource={leadSource} useFormData={() => [formData, setFormData]} />
                )}
                {isLastStep && (
                    <>
                        <div className="disclosure-container">
                            <label className="disclosure-text">
                                <strong>Disclaimer:</strong> The advertisers appearing on this website are providers of
                                insurance products and services from which Tivly receives compensation (“Sponsors”).
                                Compensation may impact where the Sponsors appear on this website (including the order
                                in which they appear). Our websites do not, and are not intended to, provide a
                                comprehensive list of all companies that may provide the products and services you are
                                seeking.
                            </label>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyQuote;
